import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link, useSearchParams } from "react-router-dom";
import { api, type UnitRow } from "../app/api";
import { DeleteCodeConfirmModal } from "../components/common/DeleteCodeConfirmModal";
import { ServerDataTable } from "../components/tables/ServerDataTable";
import { isActionMenuEventTarget, useActionMenuAutoClose, useFloatingActionMenu } from "../hooks/useActionMenuAutoClose";
import { FACILIOO_UNIT_TYPE_VALUES, formatUnitTypeLabel } from "../shared/unitTypes";

type StatusOption = "" | "aktiv" | "inaktiv";
type UnitTypeOption = "" | string;
type UnitSortField = "objektnummer" | "einheitsnummer" | "bezeichnung";

type DeleteConfirmState = {
  id: string;
  label: string;
  code: string;
  input: string[];
  error: string;
} | null;

type UnitsPageProps = {
  canCreate?: boolean;
  canUpdate?: boolean;
  canDelete?: boolean;
};

const formatNumberDE = (value?: number) => {
  const numeric = Number(value ?? 0);
  return Number.isFinite(numeric) && numeric > 0 ? new Intl.NumberFormat("de-DE").format(numeric) : "-";
};

const unitBuildingIcon = (
  <svg viewBox="0 0 24 24" fill="none">
    <rect x="5.1" y="4.8" width="13.8" height="14.8" rx="2.1" stroke="currentColor" strokeWidth="1.8" />
    <path d="M8.3 9h2.6M13.1 9h2.6M8.3 13.2h2.6M13.1 13.2h2.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M9.2 19.6v-2.9h5.6v2.9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const icons = {
  search: (
    <svg viewBox="0 0 20 20" fill="none">
      <circle cx="9" cy="9" r="5.8" stroke="currentColor" strokeWidth="1.8" />
      <path d="m13.2 13.2 4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  ),
  reset: (
    <svg viewBox="0 0 20 20" fill="none">
      <path d="M5.2 6.3V3.9H2.8M4 4a6.4 6.4 0 1 1-1.4 7.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  object: (
    <svg viewBox="0 0 20 20" fill="none">
      <path d="M6.2 5.2h7.6v9.6H6.2z" stroke="currentColor" strokeWidth="1.7" />
      <path d="M6.2 8.4h7.6M6.2 11.6h7.6M9.4 5.2v9.6M12.6 5.2v9.6" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  ),
  entrance: (
    <svg viewBox="0 0 20 20" fill="none">
      <path d="M4.2 15.7h11.6M6 15.7V5.2h8v10.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8 8h4M8 11h4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  ),
  location: (
    <svg viewBox="0 0 20 20" fill="none">
      <path d="M10 17s5.2-4.7 5.2-9a5.2 5.2 0 0 0-10.4 0c0 4.3 5.2 9 5.2 9Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <circle cx="10" cy="8" r="1.8" stroke="currentColor" strokeWidth="1.7" />
    </svg>
  ),
  type: (
    <svg viewBox="0 0 20 20" fill="none">
      <path d="M4.3 5.5h11.4M4.3 10h11.4M4.3 14.5h11.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <path d="M7.2 3.8v12.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  ),
  votes: (
    <svg viewBox="0 0 20 20" fill="none">
      <path d="M4.1 15.9h11.8M5.6 15.9V9.4M10 15.9V6.7M14.4 15.9V4.1" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  ),
  mea: (
    <svg viewBox="0 0 20 20" fill="none">
      <path d="M10 3.7 15.3 6.8v6.4L10 16.3l-5.3-3.1V6.8L10 3.7Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <path d="M10 3.7v12.6M4.7 6.8 10 10l5.3-3.2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  status: (
    <svg viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="10" r="5.9" stroke="currentColor" strokeWidth="1.7" />
      <path d="m10 7.2 1.8 2.5L10 12.8 8.2 9.7 10 7.2Z" fill="currentColor" />
    </svg>
  ),
  date: (
    <svg viewBox="0 0 20 20" fill="none">
      <path d="M5.2 3.8h6.5l2.9 2.9v8.6a1.2 1.2 0 0 1-1.2 1.2H5.2A1.2 1.2 0 0 1 4 15.3V5a1.2 1.2 0 0 1 1.2-1.2Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <path d="M6.8 9.4h6.1M6.8 12.2h5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  ),
  actions: (
    <svg viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="5.3" r="1.4" fill="currentColor" />
      <circle cx="10" cy="10" r="1.4" fill="currentColor" />
      <circle cx="10" cy="14.7" r="1.4" fill="currentColor" />
    </svg>
  )
};

const plainHeader = (label: ReactNode, icon: ReactNode) => (
  <span className="resolutions-v2-th-plain">
    <span className="resolutions-v2-th-icon" aria-hidden="true">{icon}</span>
    <span className="resolutions-v2-th-label">{label}</span>
  </span>
);

export const UnitsPage = ({ canCreate = false, canUpdate = false, canDelete = false }: UnitsPageProps) => {
  const queryClient = useQueryClient();
  const [pageSize, setPageSize] = useState(10);
  const [searchParams] = useSearchParams();
  const initialSearch = String(searchParams.get("search") ?? "").trim();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(initialSearch);
  const [objectFilter, setObjectFilter] = useState("");
  const [status, setStatus] = useState<StatusOption>("aktiv");
  const [unitTypeFilter, setUnitTypeFilter] = useState<UnitTypeOption>("");
  const [sortBy, setSortBy] = useState<UnitSortField>("objektnummer");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [openActionMenuId, setOpenActionMenuId] = useState<string | null>(null);
  const [isActionMenuHovered, setIsActionMenuHovered] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [createError, setCreateError] = useState("");
  const [editError, setEditError] = useState("");
  const [actionError, setActionError] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmState>(null);
  const [form, setForm] = useState({
    objectId: "",
    einheitsnummer: "",
    bezeichnung: "",
    einheitstyp: "" as UnitTypeOption,
    stimmrecht: "",
    mea: "",
    status: "" as StatusOption
  });
  const [editForm, setEditForm] = useState<null | {
    id: string;
    objectId: string;
    einheitsnummer: string;
    bezeichnung: string;
    einheitstyp: string;
    stimmrecht: string;
    mea: string;
    status: "aktiv" | "inaktiv";
  }>(null);

  const { data, isError } = useQuery({
    queryKey: ["units", page, pageSize, search, objectFilter, unitTypeFilter, status, sortBy, sortDir],
    queryFn: () =>
      api.units({
        page,
        pageSize,
        search,
        objectId: objectFilter || undefined,
        status: status || undefined,
        einheitstyp: unitTypeFilter || undefined,
        sortBy,
        sortDir
      }),
    placeholderData: (previousData) => previousData,
    staleTime: 15_000
  });

  const { data: objectsData } = useQuery({
    queryKey: ["objects-select"],
    queryFn: () => api.objects({ page: 1, pageSize: 200, sortBy: "objektnummer", sortDir: "asc" })
  });

  const { data: activeResolutions } = useQuery({
    queryKey: ["active-resolutions-lock"],
    queryFn: () => api.resolutions({ page: 1, pageSize: 500, status: "aktiv" })
  });

  const { data: uiSettings } = useQuery({
    queryKey: ["ui-settings-units"],
    queryFn: () => api.uiSettings()
  });

  const lockedObjectIds = useMemo(() => new Set((activeResolutions?.data ?? []).map((r) => r.objectId)), [activeResolutions?.data]);
  const objectIdByObjektnummer = useMemo(() => new Map((objectsData?.data ?? []).map((o) => [o.objektnummer, o.id])), [objectsData?.data]);

  useEffect(() => {
    setPage(1);
  }, [search, objectFilter, unitTypeFilter, status, sortBy, sortDir, pageSize]);

  useEffect(() => {
    setOpenActionMenuId(null);
  }, [page, search, objectFilter, unitTypeFilter, status, sortBy, sortDir, showCreate, editForm]);

  useEffect(() => {
    if (!openActionMenuId) return;
    const closeMenu = (event?: MouseEvent) => {
      if (event && isActionMenuEventTarget(event.target)) return;
      setOpenActionMenuId(null);
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeMenu();
    };
    window.addEventListener("mousedown", closeMenu);
    window.addEventListener("keydown", handleEscape);
    return () => {
      window.removeEventListener("mousedown", closeMenu);
      window.removeEventListener("keydown", handleEscape);
    };
  }, [openActionMenuId]);

  useActionMenuAutoClose({
    isOpen: !!openActionMenuId,
    isHovered: isActionMenuHovered,
    closeMenu: () => setOpenActionMenuId(null),
    timeoutMs: 3000
  });

  useEffect(() => {
    if (!openActionMenuId) setIsActionMenuHovered(false);
  }, [openActionMenuId]);

  const { actionMenuPosition, openActionMenuAtTrigger } = useFloatingActionMenu({
    openMenuId: openActionMenuId,
    setOpenMenuId: setOpenActionMenuId
  });

  const formatEinheitsnummer = (value: string) => {
    const trimmed = String(value ?? "").trim();
    if (!trimmed) return "";
    return /^\d+$/.test(trimmed) ? trimmed.padStart(4, "0") : trimmed;
  };

  const createMutation = useMutation({
    mutationFn: () =>
      api.createUnit({
        objectId: form.objectId,
        einheitsnummer: form.einheitsnummer.trim(),
        bezeichnung: form.bezeichnung.trim(),
        einheitstyp: form.einheitstyp,
        stimmrecht: Number(form.stimmrecht),
        mea: form.mea ? Number(form.mea) : undefined,
        status: form.status as "aktiv" | "inaktiv"
      }),
    onSuccess: async () => {
      setCreateError("");
      setShowCreate(false);
      setForm({ objectId: "", einheitsnummer: "", bezeichnung: "", einheitstyp: "", stimmrecht: "", mea: "", status: "" });
      await queryClient.invalidateQueries({ queryKey: ["units"] });
      setPage(1);
    },
    onError: (error) => setCreateError(error instanceof Error ? error.message : "Anlage fehlgeschlagen")
  });

  const editMutation = useMutation({
    mutationFn: () => {
      if (!editForm) throw new Error("Kein Datensatz ausgewählt");
      if (lockedObjectIds.has(editForm.objectId)) throw new Error("Einheit ist wegen aktivem Umlaufbeschluss gesperrt");
      return api.updateUnit(editForm.id, {
        einheitsnummer: editForm.einheitsnummer.trim(),
        bezeichnung: editForm.bezeichnung.trim(),
        einheitstyp: editForm.einheitstyp,
        stimmrecht: Number(editForm.stimmrecht),
        mea: editForm.mea ? Number(editForm.mea) : undefined,
        status: editForm.status
      });
    },
    onSuccess: async () => {
      setEditError("");
      setEditForm(null);
      await queryClient.invalidateQueries({ queryKey: ["units"] });
    },
    onError: (error) => setEditError(error instanceof Error ? error.message : "Bearbeitung fehlgeschlagen")
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deactivateUnit(id),
    onSuccess: async () => {
      setDeleteConfirm(null);
      setActionError("");
      await queryClient.invalidateQueries({ queryKey: ["units"] });
    },
    onError: (error) => setActionError(error instanceof Error ? error.message : "Löschen fehlgeschlagen")
  });

  const toggleSort = (field: UnitSortField) => {
    if (sortBy === field) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortBy(field);
    setSortDir("asc");
  };

  const sortLabel = (field: UnitSortField, label: ReactNode) => (
    <button
      type="button"
      onClick={() => toggleSort(field)}
      style={{
        border: "none",
        background: "transparent",
        padding: 0,
        margin: 0,
        cursor: "pointer",
        font: "inherit",
        color: "inherit",
        fontWeight: 700,
        display: "inline-flex",
        alignItems: "center",
        gap: 6
      }}
      title={sortBy === field ? `Sortierung: ${sortDir === "asc" ? "aufsteigend" : "absteigend"}` : "Sortierung aktivieren"}
    >
      <span>{label}</span>
      <span aria-hidden="true">{sortBy === field ? (sortDir === "asc" ? "↑" : "↓") : "↕"}</span>
    </button>
  );

  const rows = data?.data ?? [];
  const totalRows = data?.meta.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const isFrameworkV2 =
    uiSettings?.management?.uiFramework === "mockup_v2" ||
    (typeof document !== "undefined" && document.body.classList.contains("framework-v2"));
  const isUnitFormActive = showCreate || Boolean(editForm);
  const fixedViewportListLayout = isFrameworkV2 && !isUnitFormActive;

  const pageItems = useMemo(() => {
    if (totalPages <= 1) return [1];
    const start = Math.max(1, page - 1);
    const end = Math.min(totalPages, start + 2);
    const adjustedStart = Math.max(1, end - 2);
    return Array.from({ length: end - adjustedStart + 1 }, (_, i) => adjustedStart + i);
  }, [page, totalPages]);

  const resetListFilters = () => {
    setSearch("");
    setObjectFilter("");
    setUnitTypeFilter("");
    setStatus("aktiv");
    setSortBy("objektnummer");
    setSortDir("asc");
    setPage(1);
  };

  const renderSortIndicator = (field: UnitSortField) => (
    <span className="resolutions-v2-sort-indicator" aria-hidden="true">
      {sortBy === field ? (sortDir === "asc" ? "↑" : "↓") : "↕"}
    </span>
  );

  const renderV2SortHeader = (field: UnitSortField, label: ReactNode, icon: ReactNode) => (
    <button type="button" className="resolutions-v2-th-sort" onClick={() => toggleSort(field)}>
      <span className="resolutions-v2-th-icon" aria-hidden="true">{icon}</span>
      <span className="resolutions-v2-th-label">{label}</span>
      {renderSortIndicator(field)}
    </button>
  );

  const openEditForm = (r: UnitRow) => {
    setEditForm({
      id: r.id,
      objectId: objectIdByObjektnummer.get(r.objektnummer) ?? "",
      einheitsnummer: r.einheitsnummer,
      bezeichnung: r.bezeichnung,
      einheitstyp: String(r.einheitstyp ?? "").trim().toLowerCase(),
      stimmrecht: String(r.stimmrecht),
      mea: r.mea !== undefined ? String(r.mea) : "",
      status: r.status
    });
  };

  const requestDelete = (r: UnitRow) => {
    const rowObjectId = objectIdByObjektnummer.get(r.objektnummer) ?? "";
    const locked = rowObjectId ? lockedObjectIds.has(rowObjectId) : false;
    if (locked) {
      setActionError("Einheit ist wegen aktivem Umlaufbeschluss gesperrt.");
      return;
    }
    setDeleteConfirm({
      id: r.id,
      label: `${r.objektnummer} / ${r.einheitsnummer}`,
      code: String(Math.floor(1000 + Math.random() * 9000)),
      input: ["", "", "", ""],
      error: ""
    });
  };

  const columns = useMemo(
    () => [
      {
        key: "objektnummer",
        label: sortLabel("objektnummer", <><span style={{ whiteSpace: "nowrap" }}>Objekt-Nr</span><br />&nbsp;</>),
        align: "center" as const,
        width: 92,
        render: (r: UnitRow) => {
          const objectId = objectIdByObjektnummer.get(r.objektnummer);
          return objectId ? (
            <Link to={`/objects/${objectId}`} className="badge object-ref" style={{ textDecoration: "none" }}>
              {r.objektnummer}
            </Link>
          ) : (
            r.objektnummer
          );
        }
      },
      { key: "einheitsnummer", label: sortLabel("einheitsnummer", <>Einheit-Nr.<br />&nbsp;</>), align: "center" as const, width: 96, render: (r: UnitRow) => formatEinheitsnummer(r.einheitsnummer) },
      { key: "aufgang", label: <>Aufgang<br />&nbsp;</>, render: (r: UnitRow) => r.aufgangName || r.aufgangRef || "-" },
      { key: "bezeichnung", label: sortLabel("bezeichnung", <>Lage<br />&nbsp;</>) },
      { key: "einheitstyp", label: <>Einheitstyp<br />&nbsp;</>, align: "center" as const, render: (r: UnitRow) => formatUnitTypeLabel(r.einheitstyp) },
      { key: "stimmrecht", label: <>Stimmrecht<br />&nbsp;</>, align: "center" as const, render: (r: UnitRow) => formatNumberDE(r.stimmrecht) },
      { key: "mea", label: <>MEA<br />&nbsp;</>, align: "center" as const, render: (r: UnitRow) => formatNumberDE(r.mea) },
      { key: "status", label: <>Status<br />&nbsp;</>, render: (r: UnitRow) => <span className={`badge ${r.status}`}>{r.status}</span> },
      {
        key: "actions",
        label: <>Aktionen<br />&nbsp;</>,
        render: (r: UnitRow) => {
          if (!canUpdate && !canDelete) return <span style={{ color: "var(--text-soft)" }}>-</span>;
          const rowObjectId = objectIdByObjektnummer.get(r.objektnummer) ?? "";
          const locked = rowObjectId ? lockedObjectIds.has(rowObjectId) : false;
          return (
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              {canUpdate ? <button onClick={() => openEditForm(r)} disabled={locked} title={locked ? "Gesperrt wegen aktivem Umlaufbeschluss" : undefined}>Bearbeiten</button> : null}
              {canDelete ? <button onClick={() => requestDelete(r)} disabled={locked || deleteMutation.isPending} title={locked ? "Löschen bei aktivem Umlaufbeschluss gesperrt" : undefined}>Löschen</button> : null}
              {locked && (canUpdate || canDelete) ? <span className="lock-indicator" title="Gesperrt wegen aktivem Umlaufbeschluss">{"\uD83D\uDD12"}</span> : null}
            </div>
          );
        }
      }
    ],
    [canDelete, canUpdate, deleteMutation.isPending, lockedObjectIds, objectIdByObjektnummer, sortBy, sortDir]
  );

  const canCreateForm = form.objectId && form.einheitsnummer.trim() && form.einheitstyp && form.mea !== "" && Number(form.mea) >= 0 && form.status;
  const canEdit = editForm && editForm.einheitsnummer.trim() && editForm.einheitstyp && editForm.mea !== "" && Number(editForm.mea) >= 0;

  return (
    <div className={fixedViewportListLayout ? "resolutions-v2-page" : undefined}>
      <section className={`page-header panel ${isFrameworkV2 ? "resolutions-v2-page-header" : ""}`}>
        <h2 className={isFrameworkV2 ? "resolutions-v2-title" : ""} style={{ margin: 0 }}>
          {isFrameworkV2 ? <span className="resolutions-v2-title-icon" aria-hidden="true">{unitBuildingIcon}</span> : null}
          {isFrameworkV2 ? (
            <span className="resolutions-v2-title-copy">
              <span className="resolutions-v2-title-main">Einheiten</span>
              <span className="resolutions-v2-title-sub">Übersicht aller Einheiten und Nutzungsdaten</span>
            </span>
          ) : (
            <span>Einheiten</span>
          )}
        </h2>
        {canCreate ? (
          <button className={isFrameworkV2 ? "resolutions-v2-create-btn" : ""} onClick={() => setShowCreate((v) => !v)}>
            {isFrameworkV2 && !showCreate ? (
              <span className="resolutions-v2-create-btn-icon" aria-hidden="true">
                <svg viewBox="0 0 20 20" fill="none">
                  <path d="M10 4.2v11.6M4.2 10h11.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </span>
            ) : null}
            <span>{showCreate ? "Schließen" : "Neue Einheit"}</span>
          </button>
        ) : null}
      </section>

      {canCreate && showCreate ? (
        <section className="panel create-form">
          <h3 style={{ marginTop: 0 }}>Einheit anlegen</h3>
          <div className="form-grid">
            <div className="form-field">
              <label>Objekt*</label>
              <select value={form.objectId} onChange={(e) => setForm({ ...form, objectId: e.target.value })}>
                <option value="">Objekt wählen*</option>
                {(objectsData?.data ?? []).map((o) => (
                  <option key={o.id} value={o.id}>{`${o.objektnummer} - ${o.objektname}${lockedObjectIds.has(o.id) ? " (gesperrt)" : ""}`}</option>
                ))}
              </select>
            </div>
            <div className="form-field"><label>Einheitsnummer*</label><input value={form.einheitsnummer} onChange={(e) => setForm({ ...form, einheitsnummer: e.target.value })} /></div>
            <div className="form-field"><label>Lage</label><input value={form.bezeichnung} onChange={(e) => setForm({ ...form, bezeichnung: e.target.value })} /></div>
            <div className="form-field"><label>Einheitstyp*</label><select value={form.einheitstyp} onChange={(e) => setForm({ ...form, einheitstyp: e.target.value as UnitTypeOption })}><option value="">Einheitstyp wählen*</option>{FACILIOO_UNIT_TYPE_VALUES.map((value) => <option key={value} value={value}>{formatUnitTypeLabel(value)}</option>)}</select></div>
            <div className="form-field"><label>Stimmrecht</label><input type="number" min="0" step="0.01" value={form.stimmrecht} onChange={(e) => setForm({ ...form, stimmrecht: e.target.value })} /></div>
            <div className="form-field"><label>MEA*</label><input type="number" min="0" step="0.01" value={form.mea} onChange={(e) => setForm({ ...form, mea: e.target.value })} /></div>
            <div className="form-field"><label>Status*</label><select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as StatusOption })}><option value="">Status wählen*</option><option value="aktiv">Aktiv</option><option value="inaktiv">Inaktiv</option></select></div>
          </div>
          <div className="form-actions">
            <button disabled={!canCreateForm || createMutation.isPending || lockedObjectIds.has(form.objectId)} title={lockedObjectIds.has(form.objectId) ? "Gesperrt wegen aktivem Umlaufbeschluss" : undefined} onClick={() => createMutation.mutate()}>
              {createMutation.isPending ? "Speichere..." : "Einheit speichern"}
            </button>
            {createError ? <span className="error-text">{createError}</span> : null}
          </div>
        </section>
      ) : null}

      {!showCreate && canUpdate && editForm ? (
        <section className="panel create-form">
          <h3 style={{ marginTop: 0 }}>Einheit bearbeiten</h3>
          <div className="form-grid">
            <div className="form-field"><label>Einheitsnummer*</label><input value={editForm.einheitsnummer} onChange={(e) => setEditForm({ ...editForm, einheitsnummer: e.target.value })} /></div>
            <div className="form-field"><label>Lage</label><input value={editForm.bezeichnung} onChange={(e) => setEditForm({ ...editForm, bezeichnung: e.target.value })} /></div>
            <div className="form-field"><label>Einheitstyp*</label><select value={editForm.einheitstyp} onChange={(e) => setEditForm({ ...editForm, einheitstyp: e.target.value })}>{FACILIOO_UNIT_TYPE_VALUES.map((value) => <option key={value} value={value}>{formatUnitTypeLabel(value)}</option>)}</select></div>
            <div className="form-field"><label>Stimmrecht</label><input type="number" min="0" step="0.01" value={editForm.stimmrecht} onChange={(e) => setEditForm({ ...editForm, stimmrecht: e.target.value })} /></div>
            <div className="form-field"><label>MEA*</label><input type="number" min="0" step="0.01" value={editForm.mea} onChange={(e) => setEditForm({ ...editForm, mea: e.target.value })} /></div>
            <div className="form-field"><label>Status</label><select value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value as "aktiv" | "inaktiv" })}><option value="aktiv">Aktiv</option><option value="inaktiv">Inaktiv</option></select></div>
          </div>
          <div className="form-actions">
            <button disabled={!canEdit || editMutation.isPending || (editForm ? lockedObjectIds.has(editForm.objectId) : false)} title={editForm && lockedObjectIds.has(editForm.objectId) ? "Gesperrt wegen aktivem Umlaufbeschluss" : undefined} onClick={() => editMutation.mutate()}>
              {editMutation.isPending ? "Speichere..." : "Änderungen speichern"}
            </button>
            <button onClick={() => setEditForm(null)}>Abbrechen</button>
            {editError ? <span className="error-text">{editError}</span> : null}
          </div>
        </section>
      ) : null}

      {!isUnitFormActive && isError ? <section className="panel">Fehler beim Laden der Einheiten.</section> : null}
      {!isUnitFormActive && actionError ? <section className="panel error-text">{actionError}</section> : null}
      {!isUnitFormActive ? (
        isFrameworkV2 ? (
          <section className="panel resolutions-v2-list-panel">
            <div className="resolutions-v2-filterbar">
              <div className="resolutions-v2-filter-fields">
                <label className="resolutions-v2-filter-input">
                  <span className="resolutions-v2-filter-icon" aria-hidden="true">{icons.search}</span>
                  <input placeholder="Suche..." value={search} onChange={(e) => setSearch(e.target.value)} />
                </label>
                <select value={objectFilter} onChange={(e) => setObjectFilter(e.target.value)}>
                  <option value="">Alle Objekt-Nr</option>
                  {(objectsData?.data ?? []).map((o) => <option key={o.id} value={o.id}>{o.objektnummer}</option>)}
                </select>
                <select value={unitTypeFilter} onChange={(e) => setUnitTypeFilter(e.target.value as UnitTypeOption)}>
                  <option value="">Alle Einheitstypen</option>
                  {FACILIOO_UNIT_TYPE_VALUES.map((value) => <option key={value} value={value}>{formatUnitTypeLabel(value)}</option>)}
                </select>
                <select value={status} onChange={(e) => setStatus(e.target.value as StatusOption)}>
                  <option value="">Alle Status</option>
                  <option value="aktiv">Aktiv</option>
                  <option value="inaktiv">Inaktiv</option>
                </select>
              </div>
              <button type="button" className="resolutions-v2-reset-btn" onClick={resetListFilters}>
                <span className="resolutions-v2-reset-btn-icon" aria-hidden="true">{icons.reset}</span>
                <span>Filter zurücksetzen</span>
              </button>
            </div>

            <div className="resolutions-v2-row-count">Zeilen: {rows.length} / Gesamt: {totalRows}</div>

            <div className="resolutions-v2-table-wrap">
              <table className="resolutions-v2-table units-v2-table">
                <colgroup>
                  <col /><col /><col /><col /><col /><col /><col /><col /><col style={{ width: "92px" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>{renderV2SortHeader("objektnummer", "Objekt-Nr", icons.object)}</th>
                    <th>{renderV2SortHeader("einheitsnummer", "Einheit-Nr", unitBuildingIcon)}</th>
                    <th>{plainHeader("Aufgang", icons.entrance)}</th>
                    <th>{renderV2SortHeader("bezeichnung", "Lage", icons.location)}</th>
                    <th>{plainHeader("Einheitstyp", icons.type)}</th>
                    <th>{plainHeader("Stimmrecht", icons.votes)}</th>
                    <th>{plainHeader("MEA", icons.mea)}</th>
                    <th>{plainHeader("Status", icons.status)}</th>
                    <th className="resolutions-v2-actions-head" aria-label="Aktionen" title="Aktionen">{plainHeader("", icons.actions)}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const objectId = objectIdByObjektnummer.get(r.objektnummer);
                    const locked = objectId ? lockedObjectIds.has(objectId) : false;
                    const menuId = r.id;
                    return (
                      <tr key={r.id}>
                        <td>{objectId ? <Link to={`/objects/${objectId}`} className="badge object-number" style={{ textDecoration: "none" }}>{r.objektnummer}</Link> : <span className="badge object-number">{r.objektnummer}</span>}</td>
                        <td>{formatEinheitsnummer(r.einheitsnummer)}</td>
                        <td>{r.aufgangName || r.aufgangRef || "-"}</td>
                        <td className="resolutions-v2-object-cell">{r.bezeichnung || "-"}</td>
                        <td>{formatUnitTypeLabel(r.einheitstyp)}</td>
                        <td>{formatNumberDE(r.stimmrecht)}</td>
                        <td>{formatNumberDE(r.mea)}</td>
                        <td><span className={`badge ${r.status}`}>{r.status}</span></td>
                        <td>
                          {!canUpdate && !canDelete ? (
                            <span style={{ color: "var(--text-soft)" }}>-</span>
                          ) : (
                          <div className="resolutions-v2-action-wrap" onClick={(e) => e.stopPropagation()}>
                            <button
                              type="button"
                              className="resolutions-v2-action-trigger"
                              aria-label={`Aktionen für Einheit ${r.einheitsnummer}`}
                              aria-expanded={openActionMenuId === menuId}
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (openActionMenuId === menuId) {
                                  setOpenActionMenuId(null);
                                  return;
                                }
                                openActionMenuAtTrigger(menuId, e.currentTarget);
                              }}
                            >
                              ⋮
                            </button>
                            {openActionMenuId === menuId && actionMenuPosition && typeof document !== "undefined" ? createPortal(
                              <div
                                className="resolutions-v2-action-menu resolutions-v2-action-menu--fixed"
                                style={{ left: actionMenuPosition.left, top: actionMenuPosition.top }}
                                onMouseDown={(e) => e.stopPropagation()}
                                onMouseEnter={() => setIsActionMenuHovered(true)}
                                onMouseLeave={() => setIsActionMenuHovered(false)}
                              >
                                {canUpdate ? (
                                  <button
                                    type="button"
                                    disabled={locked}
                                    onClick={() => {
                                      if (locked) {
                                        setActionError("Einheit ist wegen aktivem Umlaufbeschluss gesperrt.");
                                        setOpenActionMenuId(null);
                                        return;
                                      }
                                      openEditForm(r);
                                      setOpenActionMenuId(null);
                                    }}
                                  >
                                    Bearbeiten
                                  </button>
                                ) : null}
                                {canDelete ? (
                                  <button
                                    type="button"
                                    disabled={locked || deleteMutation.isPending}
                                    onClick={() => {
                                      requestDelete(r);
                                      setOpenActionMenuId(null);
                                    }}
                                  >
                                    Löschen
                                  </button>
                                ) : null}
                              </div>,
                              document.body
                            ) : null}
                          </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="resolutions-v2-pagination">
              <div className="resolutions-v2-page-size">
                <select value={String(pageSize)} onChange={(e) => setPageSize(Number(e.target.value) || 10)}>
                  <option value="10">10 pro Seite</option>
                  <option value="20">20 pro Seite</option>
                  <option value="50">50 pro Seite</option>
                </select>
              </div>
              <div className="resolutions-v2-page-center">
                <button type="button" className="resolutions-v2-page-arrow" onClick={() => setPage((prev) => Math.max(1, prev - 1))} disabled={page <= 1}>‹</button>
                {pageItems.map((pageItem) => (
                  <button key={pageItem} type="button" className={`resolutions-v2-page-chip ${pageItem === page ? "active" : ""}`} onClick={() => setPage(pageItem)}>{pageItem}</button>
                ))}
                <button type="button" className="resolutions-v2-page-arrow" onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))} disabled={page >= totalPages}>›</button>
                <span className="resolutions-v2-page-info">Seite {page} von {totalPages}</span>
              </div>
              <div className="resolutions-v2-page-next-wrap">
                <button type="button" className="resolutions-v2-next-btn" onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))} disabled={page >= totalPages}>
                  <span>Weiter</span>
                  <span aria-hidden="true">›</span>
                </button>
              </div>
            </div>
          </section>
        ) : (
          <ServerDataTable
            columns={columns}
            rows={rows}
            total={totalRows}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            bodyMaxHeight={560}
            topContent={
              <section className="filterbar" style={{ margin: 0 }}>
                <input placeholder="Suche" value={search} onChange={(e) => setSearch(e.target.value)} />
                <select value={objectFilter} onChange={(e) => setObjectFilter(e.target.value)}>
                  <option value="">Alle Objekt-Nr</option>
                  {(objectsData?.data ?? []).map((o) => <option key={o.id} value={o.id}>{o.objektnummer}</option>)}
                </select>
                <select value={unitTypeFilter} onChange={(e) => setUnitTypeFilter(e.target.value as UnitTypeOption)}>
                  <option value="">Alle Einheitstypen</option>
                  {FACILIOO_UNIT_TYPE_VALUES.map((value) => <option key={value} value={value}>{formatUnitTypeLabel(value)}</option>)}
                </select>
                <select value={status} onChange={(e) => setStatus(e.target.value as StatusOption)}>
                  <option value="">Alle Status</option>
                  <option value="aktiv">Aktiv</option>
                  <option value="inaktiv">Inaktiv</option>
                </select>
              </section>
            }
          />
        )
      ) : null}

      <DeleteCodeConfirmModal
        open={!!deleteConfirm}
        message={deleteConfirm ? `Einheit ${deleteConfirm.label} soll gelöscht werden.` : ""}
        code={deleteConfirm?.code ?? ""}
        input={deleteConfirm?.input ?? ["", "", "", ""]}
        error={deleteConfirm?.error}
        isPending={deleteMutation.isPending}
        onClose={() => setDeleteConfirm(null)}
        onInputChange={(next) => {
          if (!deleteConfirm) return;
          setDeleteConfirm({ ...deleteConfirm, input: next });
        }}
        onErrorReset={() => {
          if (!deleteConfirm || !deleteConfirm.error) return;
          setDeleteConfirm({ ...deleteConfirm, error: "" });
        }}
        onConfirm={() => {
          if (!deleteConfirm) return;
          const enteredCode = deleteConfirm.input.join("");
          if (enteredCode !== deleteConfirm.code) {
            setDeleteConfirm({ ...deleteConfirm, error: "Sicherheitscode falsch eingegeben." });
            return;
          }
          deleteMutation.mutate(deleteConfirm.id);
        }}
      />
    </div>
  );
};
